// src/components/WideSidebar.js

import React, {
  useState,
  useEffect,
  useRef,
  useContext
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './WideSidebar.css';
import { useAuth } from '../context/AuthContext';
import {
  doc,
  onSnapshot,
  updateDoc
} from 'firebase/firestore';
import { getAllAssistants } from '../services/openaiService';
import { SearchContext } from '../context/SearchContext';
import { SelectedTeamMemberContext } from '../context/SelectedTeamMemberContext';
import useTeamMembers from '../hooks/useTeamMembers'; // Hook que unifica usuarios reales y/o ficticios
import { db } from '../firebase';


// Función auxiliar para comparar objetos superficialmente
function shallowEqualObj(objA, objB) {
  if (objA === objB) return true;
  const aKeys = Object.keys(objA);
  const bKeys = Object.keys(objB);
  if (aKeys.length !== bKeys.length) return false;
  for (let key of aKeys) {
    if (objA[key] !== objB[key]) return false;
  }
  return true;
}

const WideSidebar = ({ isVisible, crmColumns }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const { setSearchTerm } = useContext(SearchContext);
  const { selectedTeamMember, setSelectedTeamMember } = useContext(SelectedTeamMemberContext);

  // Detectar rutas
  const isThreadsPage   = location.pathname.startsWith('/messages');
  const isChatPage      = location.pathname.startsWith('/chat');
  const isCrmPage       = location.pathname === '/crm';
  const isAnalyticsPage = location.pathname === '/analytics' || location.pathname === '/support';
  const isProfilePage   = location.pathname === '/profile';

  // Detectar /messages/:assistantId
  const messagesMatch = location.pathname.match(/^\/messages\/([^/]+)/);
  const currentAssistantId = messagesMatch ? messagesMatch[1] : null;

  // Detectar /chat/:assistantId
  const chatMatch = location.pathname.match(/^\/chat\/([^/]+)/);
  const currentChatAssistantId = chatMatch ? chatMatch[1] : null;

  // Estado local para IA
  const [assistantAiEnabled, setAssistantAiEnabled] = useState(true);

  // Detectar si estamos en /bubble/:assistantId
  const bubbleMatch        = location.pathname.match(/^\/bubble\/([^/]+)/);
  const isBubblePage       = !!bubbleMatch;
  const bubbleAssistantId  = isBubblePage ? bubbleMatch[1] : null;

  // Estado local de búsqueda
  const [agentSearchTerm, setAgentSearchTerm] = useState('');
  const [filteredAgents, setFilteredAgents]   = useState([]);
  const [assistants, setAssistants]          = useState([]);
  const [loading, setLoading]                = useState(true);


  // Hook que retorna la lista de miembros (reales + ficticios) con conteo
  const { teamMembersWithCounts } = useTeamMembers(currentUser, isThreadsPage);


  // Respuesta IA global
  const [globalAutoResponseEnabled, setGlobalAutoResponseEnabled] = useState(true);

  // Estado para el switch de Seguimiento Global
  const [globalTrackingEnabled, setGlobalTrackingEnabled] = useState(false);

  // Referencia para desuscribir listeners de assistants
  const unsubscribeAssistantsListenersRef = useRef([]);

  const [assistantLoaded, setAssistantLoaded] = useState({});

  // Al inicio del componente
const [assistantChatsCount, setAssistantChatsCount] = useState({});


  // ------------------------------------------------
  // FUNCIONES PARA ACTIVAR/DESACTIVAR “SEGUIMIENTO”
  // ------------------------------------------------
  const handleToggleGlobalTracking = async () => {
    if (!currentAssistantId) {
      alert('No hay assistantId en la URL');
      return;
    }
    const newValue = !globalTrackingEnabled;
  
    // Mostrar alerta cada vez que se hace toggle
    alert(`Seguimiento global: ${newValue ? 'Encendido' : 'Apagado'}`);
  
    setGlobalTrackingEnabled(newValue);
  
    // Resto de la lógica que ya tenías...
    await updateDoc(doc(db, "assistants", currentAssistantId), {
      globalTrackingEnabled: newValue
    });
  
    try {
      if (newValue) {
        await enableTrackingInAllThreads(currentAssistantId);
        await fetch('https://backend.thethingapp.com/api/trigger-check-tracking', {
          method: 'POST'
        });
      } else {
        await disableTrackingInAllThreads(currentAssistantId);
      }
    } catch (error) {
      console.error('Error al actualizar el seguimiento global:', error);
    }
  };
  
  useEffect(() => {
    if (!currentAssistantId) {
      setGlobalTrackingEnabled(false);
      return;
    }
    const docRef = doc(db, "assistants", currentAssistantId);
    const unsubscribe = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setGlobalTrackingEnabled(data.globalTrackingEnabled ?? false);
      }
    });
  
    return () => unsubscribe();
  }, [currentAssistantId]);
  
  // Desactiva el tracking (trackingActive = false) en todos los threads
  // ✅ OPTIMIZADO: Usa endpoint del backend para evitar lecturas masivas desde el cliente
  async function disableTrackingInAllThreads(assistantId) {
    try {
      const resp = await fetch('https://backend.thethingapp.com/api/bulk-update-threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assistantId, field: 'trackingActive', value: false })
      });
      const data = await resp.json();
      if (data.success) {
        console.log(`Se desactivó el seguimiento en ${data.updated} threads de ${assistantId}`);
      } else {
        console.error('Error desactivando tracking:', data.error);
      }
    } catch (err) {
      console.error('Error en disableTrackingInAllThreads:', err);
    }
  }

useEffect(() => {
// reseteamos flags de carga para mostrar spinner otra vez si la lista cambia
  setAssistantLoaded({});
  const unsubscribers = [];

  assistants.forEach((agent) => {
    const ref = doc(db, 'assistants', agent.id);
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.exists() ? snap.data() : {};
      setAssistantChatsCount(prev => ({
        ...prev,
        [agent.id]: data.chatscount ?? 0
      }));
      // ← AÑADE desde aquí ↓
      setAssistantLoaded(prev => ({
        ...prev,
        [agent.id]: true
      }));
      // ← hasta aquí
    });
    unsubscribers.push(unsub);
  });

  return () => unsubscribers.forEach(u => u());
}, [assistants]);



  // Activa el tracking (trackingActive = true) en todos los threads
  // ✅ OPTIMIZADO: Usa endpoint del backend para evitar lecturas masivas desde el cliente
  async function enableTrackingInAllThreads(assistantId) {
    try {
      const resp = await fetch('https://backend.thethingapp.com/api/bulk-update-threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assistantId, field: 'trackingActive', value: true })
      });
      const data = await resp.json();
      if (data.success) {
        console.log(`Se activó el seguimiento en ${data.updated} threads de ${assistantId}`);
      } else {
        console.error('Error activando tracking:', data.error);
      }
    } catch (err) {
      console.error('Error en enableTrackingInAllThreads:', err);
    }
  }

  // ----------------------------------------------
  // EFECTO: Toggle assistant AI en “assistants”
  // ----------------------------------------------
  useEffect(() => {
    if (!currentAssistantId) {
      setAssistantAiEnabled(true);
      return;
    }
    const docRef = doc(db, 'assistants', currentAssistantId);
    const unsubscribe = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setAssistantAiEnabled(data.autoResponseEnabled ?? true);
      } else {
        setAssistantAiEnabled(true);
      }
    });
    return () => unsubscribe();
  }, [currentAssistantId]);

  // Funciones para des/habilitar la IA en TODOS los threads de un assistant
  // ✅ OPTIMIZADO: Usa endpoint del backend para evitar lecturas masivas desde el cliente
  async function disableAiInAllThreads(assistantId) {
    try {
      const resp = await fetch('https://backend.thethingapp.com/api/bulk-update-threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assistantId, field: 'autoResponseEnabled', value: false })
      });
      const data = await resp.json();
      if (!data.success) console.error('Error desactivando IA:', data.error);
    } catch (err) {
      console.error('Error en disableAiInAllThreads:', err);
    }
  }

  async function enableAiInAllThreads(assistantId) {
    try {
      const resp = await fetch('https://backend.thethingapp.com/api/bulk-update-threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assistantId, field: 'autoResponseEnabled', value: true })
      });
      const data = await resp.json();
      if (!data.success) console.error('Error activando IA:', data.error);
    } catch (err) {
      console.error('Error en enableAiInAllThreads:', err);
    }
  }

  const handleToggleAssistantAi = async () => {
    if (!currentAssistantId) {
      alert('No hay assistantId en la URL');
      return;
    }
    const newValue = !assistantAiEnabled;
    setAssistantAiEnabled(newValue);

    // (1) Actualizamos en “assistants”
    await updateDoc(doc(db, 'assistants', currentAssistantId), {
      autoResponseEnabled: newValue,
    });

    // (2) Apagar/encender en todos los threads
    if (!newValue) {
      await disableAiInAllThreads(currentAssistantId);
    } else {
      await enableAiInAllThreads(currentAssistantId);
    }
  };

  // =========================================================
  // (1) Leer preferencia global IA en la colección “users”
  // =========================================================
  useEffect(() => {
    if (!currentUser) return;
    const userDocRef = doc(db, 'users', currentUser.uid);

    const unsubscribe = onSnapshot(userDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (typeof data.globalAutoResponseEnabled === 'boolean') {
          setGlobalAutoResponseEnabled(data.globalAutoResponseEnabled);
        } else {
          setGlobalAutoResponseEnabled(true);
        }
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleToggleGlobalAutoResponse = async () => {
    const newValue = !globalAutoResponseEnabled;
    setGlobalAutoResponseEnabled(newValue);
    if (!currentUser) return;

    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        globalAutoResponseEnabled: newValue,
      });
    } catch (error) {
      console.error('Error al actualizar globalAutoResponseEnabled:', error);
    }
  };

  // =========================================================
  // (2) Obtener la lista de asistentes del usuario (Hook)
  // =========================================================
  useEffect(() => {
    const fetchAssistants = async () => {
      if (!currentUser) return;
      setLoading(true);
      try {
        const assistantsData = await getAllAssistants(currentUser.uid);
        setAssistants(assistantsData);
      } catch (error) {
        console.error('Error al obtener asistentes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAssistants();
  }, [currentUser]);

  // Búsqueda local de “Mis agentes”
  useEffect(() => {
    if (agentSearchTerm.trim() === '') {
      setFilteredAgents(assistants);
    } else {
      const term = agentSearchTerm.toLowerCase();
      setFilteredAgents(
        assistants.filter((agent) =>
          agent.name.toLowerCase().includes(term)
        )
      );
    }
  }, [assistants, agentSearchTerm]);

  

  

  // =================================
  // FUNCIONES DE NAVEGACIÓN
  // =================================
  const handleAgentSearchTermChange = (term) => {
    setAgentSearchTerm(term);
  };

  const handleSelectAgent = (agent) => {
    setSelectedTeamMember(null);
    navigate(`/messages/${agent.id}`);
  };

  const handleSelectTeamMemberClick = (member) => {
    setSelectedTeamMember(member);
    // Ejemplo de ruta => "/messages/team-{member.id}"
    navigate(`/messages/team-${member.id}`);
  };

  

  // =========================
  // Render principal
  // =========================
  return (
    <div className={`wide-sidebar ${isVisible ? 'visible' : 'hidden'}`}>
      {isBubblePage ? (
        // (1) BUBBLE PAGE
        <div className="wide-sidebar-content">
          <h2>Prueba tu agente</h2>
          <ul className="scrollable-list">
            <li
              className="list-item"
              onClick={() => navigate(`/chat/${bubbleAssistantId}`)}
            >
              Volver al entrenamiento
            </li>
          </ul>
        </div>
      ) : (
        // (2) OTRAS RUTAS
        <>
          {isThreadsPage ? (
            // (2A) /messages
            <div className="wide-sidebar-content">
              <h2 className="titulos-threads">Mis agentes</h2>

              <div className="search-container">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Buscar"
                  value={agentSearchTerm}
                  onChange={(e) => handleAgentSearchTermChange(e.target.value)}
                />
              </div>

              {/* Lista de AGENTES (WhatsApp) */}
              <ul className="scrollable-list">
                {loading ? (
                  <li className="list-item">Cargando...</li>
                ) : filteredAgents.length > 0 ? (
                  filteredAgents.map((agent) => (
<li
  key={agent.id}
  className={`list-item ${currentAssistantId === agent.id ? 'active' : ''}`}
  onClick={() => handleSelectAgent(agent)}
>
  <div className="agent-info">
    <p className="agent-name">{agent.name}</p>
<span className="thread-count">
  {assistantLoaded[agent.id]
    ? (assistantChatsCount[agent.id] ?? 0)
    : <div className="custom-spinner" />
  }
</span>

  </div>
</li>

                  ))
                ) : (
                  <li className="list-item">No se encontraron agentes</li>
                )}

                {/* Lista de TEAM MEMBERS (reales o ficticios unificados) */}
                {teamMembersWithCounts.length > 0 &&
                  teamMembersWithCounts.map((member) => (
                    <li
                      key={`member-${member.id}`}
                      className="list-item"
                      onClick={() => handleSelectTeamMemberClick(member)}
                    >
                      <div className="agent-info">
                        <p className="agent-name">
                          {member.nombre} {member.apellido}
                        </p>
                        <span className="thread-count">
                          {member.conversationCount || 0}
                        </span>
                      </div>
                    </li>
                  ))}
              </ul>

              {/* Switch para IA del assistant actual */}
              {currentAssistantId && (
                <div className="global-autoresponse-switch-container">
                  <span className="global-autoresponse-label">Respuesta IA Global</span>
                  <label className="switch-ia-global">
                    <input
                      type="checkbox"
                      checked={assistantAiEnabled}
                      onChange={handleToggleAssistantAi}
                    />
                    <span className="slider pending-slider"></span>
                  </label>
                </div>
              )}

              {/* Switch para seguimiento global */}
              {currentAssistantId && (
  <div className="global-tracking-button-container">
    <button
      className={`global-tracking-button ${globalTrackingEnabled ? 'on' : 'off'}`}
      onClick={handleToggleGlobalTracking}
    >
      {globalTrackingEnabled ? 'Seguimiento global' : 'Seguimiento global'}
    </button>
  </div>
)}

            </div>
          
          ) : isChatPage ? (
            // (2B) /chat
            <div className="wide-sidebar-content">
              <h2>Entrenador <br /> de Agentes</h2>
              <ul className="scrollable-list">
                {loading ? (
                  <li className="list-item">Cargando...</li>
                ) : assistants.length > 0 ? (
                  assistants.map((assistant) => (
                    <li
                      key={assistant.id}
                      className={`list-item ${currentChatAssistantId === assistant.id ? 'active' : ''}`}
                      onClick={() => navigate(`/chat/${assistant.id}`)}
                    >
                      {assistant.name}
                    </li>
                  ))
                ) : (
                  <li className="list-item">No hay agentes disponibles</li>
                )}
              </ul>
            </div>
          
          ) : isCrmPage ? (
            // (2C) /crm
            <div className="wide-sidebar-content">
              <h2>Panel de <br />seguimiento</h2>
              <div className="search-container">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Buscar por nombre o teléfono"
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          
          ) : isAnalyticsPage ? (
            // (2D) /analytics o /support
            <div className="wide-sidebar-content">
              <h2>Próximamente</h2>
            </div>
          
          ) : isProfilePage ? (
            // (2E) /profile
            <div className="wide-sidebar-content">
              <h2>Perfil</h2>

            </div>
          
          ) : (
            // (2F) Ruta default
            <div className="wide-sidebar-content">
              <h2>Comenzar <br /> The Thing</h2>
              <ul className="scrollable-list">
                <li className="list-item"
                onClick={() => {
                  document
                    .getElementById('misagentes')
                    ?.scrollIntoView({ behavior: 'smooth' });
                }}
                >Mis Agentes</li>
                <li className="list-item"
                onClick={() => {
                  document
                    .getElementById('basesdedatos')
                    ?.scrollIntoView({ behavior: 'smooth' });
                }}
                >Mis Bases de Datos</li>
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WideSidebar;
